import { InvalidDiagnosticProcedureIdentityError } from "./diagnostic-procedure.errors";
import { InvalidInstanceError, InvalidInstanceTransitionError, UnknownMemberError } from "./errors.model";
import type { VersionedCanonical } from "./lib/canonical";
import type { DomainId } from "./lib/domain-id";
import { type ObservationLevel, observationLevelsProblem } from "./lib/observation-level";
import type { PlannedTrial, PlannedTrialId } from "./lib/planned-trial";
import type { MemberId } from "./member-identity";
import type { ObservationId } from "./observation.model";
import type { OrderId, OrderParameterDeclaration, OrderSetId } from "./order-set.model";
import type { PatientId } from "./patient.model";
import type { PatientDocumentId } from "./patient-document.model";
import type { PractitionerId } from "./practitioner.model";
import type { ProcedureTimeSource } from "./procedure-time.model";
import {
  assertTrialSnapshot,
  assertTrialValues,
  type ProcedureTrial,
  type ProcedureTrialDefinitionSpec,
  type ProcedureTrialFacts,
} from "./procedure-trial.model";
import {
  type AnsweredValue,
  answeredValueProblem,
  type DiagnosticComponentValue,
  type ParameterValue,
  validateValue,
} from "./value.model";
import type { VisitId } from "./visit.model";

export type ProcedureId = DomainId<"ProcedureId">;

export function asProcedureId(value: string): ProcedureId {
  if (!value) throw new Error("ProcedureId cannot be empty");
  return value as ProcedureId;
}

export interface ProcedureDeclarationComponent {
  id: MemberId;
  label: string;
  value: DiagnosticComponentValue;
}
export interface ProcedureDeclarationObservation {
  id: MemberId;
  label: string;
  repeatable: boolean;
  noteLabel?: string;
  components: readonly ProcedureDeclarationComponent[];
}
export interface ProcedureDeclaration {
  definitionUrl: string;
  version: number;
  name: string;
  description?: string;
  observations: readonly ProcedureDeclarationObservation[];
  procedureNoteLabel?: string;
}
export type ProcedureStatus = "inProgress" | "performed" | "unableToComplete" | "didNotPerform" | "enteredInError";

const TRANSITIONS: Record<ProcedureAction, { from: readonly ProcedureStatus[]; to: ProcedureStatus }> = {
  complete: { from: ["inProgress"], to: "performed" },
  unableToComplete: { from: ["inProgress"], to: "unableToComplete" },
  markDidNotPerform: { from: ["inProgress"], to: "didNotPerform" },
  reopen: { from: ["performed", "unableToComplete"], to: "inProgress" },
  retract: { from: ["didNotPerform"], to: "enteredInError" },
  reinstateDidNotPerform: { from: ["enteredInError"], to: "didNotPerform" },
  resume: { from: ["enteredInError"], to: "inProgress" },
};
type ProcedureAction =
  | "complete"
  | "unableToComplete"
  | "markDidNotPerform"
  | "reopen"
  | "retract"
  | "reinstateDidNotPerform"
  | "resume";

const TERMINAL: ReadonlySet<ProcedureStatus> = new Set([
  "performed",
  "unableToComplete",
  "didNotPerform",
  "enteredInError",
]);

export type ProcedureObservationLifecycle =
  | { status: "Final" }
  | { status: "Corrected" }
  | { status: "EnteredInError" };

export interface ProcedureObservationComponent {
  componentId: MemberId;
  value: AnsweredValue;
}

export interface ProcedureRecordedObservation {
  trialId?: ObservationId;
  derivedFrom?: { documentId: PatientDocumentId; writtenBy: "Document" | "Hand" };
  observationId: ObservationId;

  definitionId: MemberId;

  observedAt: string;
  components: ProcedureObservationComponent[];
  note?: string;
  lifecycle: ProcedureObservationLifecycle;
}

export interface ProcedureObservationSpec {
  id: MemberId;
  label: string;

  repeatable: boolean;
  components: readonly { id: MemberId; value: ParameterValue }[];
}

/** Resolved exact revision, including effective device parameter declarations. */
export interface ProcedureDefinitionSpec {
  plannedTrials?: readonly PlannedTrial[];
  url: string;
  version: number;
  observations: readonly ProcedureObservationSpec[];
  parameters: readonly OrderParameterDeclaration[];
}

export interface ProcedurePerformedPeriod {
  start: string;
  end?: string;
  startSource?: ProcedureTimeSource;
  endSource?: ProcedureTimeSource;
}

export interface ProcedureOutcomeFacts extends ProcedureCompletionFacts {
  patientId: PatientId;
  visitId: VisitId;
  orderId: OrderId;
  outcome: "performed" | "unableToComplete" | "didNotPerform";
  note?: string;
}

export interface ProcedureCompletionFacts {
  performerIds?: readonly PractitionerId[];
}

/** Durable provenance; patient-specific work may originate outside an order. */
export type ProcedureOrigin = { kind: "ordered"; orderSetId: OrderSetId; orderId: OrderId } | { kind: "unplanned" };

/** Trusted order facts supplied by the caller; never stored on the Procedure. */
export interface ProcedureOrderFacts {
  patientId: PatientId;
  visitId: VisitId;
  definition: VersionedCanonical;
  executable: boolean;
  alreadyFulfilled: boolean;
}

export interface ProcedureProps {
  declaration?: ProcedureDeclaration;
  kind: "diagnostic" | "treatment";
  trials: ProcedureTrial[];
  observationLevels?: readonly ObservationLevel[];
  id: ProcedureId;
  origin: ProcedureOrigin;
  patientId: PatientId;
  visitId: VisitId;

  definition: VersionedCanonical;
  status: ProcedureStatus;
  performerIds: PractitionerId[];
  performedPeriod?: ProcedurePerformedPeriod;

  note?: string;
  observations: ProcedureRecordedObservation[];

  uncaptured: MemberId[];
}

export type ProcedureSnapshot = ProcedureProps;

export interface ProcedureCreationFacts {
  id: ProcedureId;
  declaration?: ProcedureDeclaration;
  kind: "diagnostic" | "treatment";
  observationLevels?: readonly ObservationLevel[];
  origin:
    | { kind: "ordered"; orderSetId: OrderSetId; orderId: OrderId; facts: ProcedureOrderFacts }
    | { kind: "unplanned" };
  patientId: PatientId;
  visitId: VisitId;

  definition: VersionedCanonical;
  note?: string;
}

export interface ProcedureBeginFacts extends ProcedureCreationFacts {
  performedPeriod?: ProcedurePerformedPeriod;
  performerIds?: readonly PractitionerId[];
}

/**
 * One patient-specific execution of a pinned ProcedureDefinition.
 * The caller resolves that revision and supplies its executionSpec(); no I/O
 * lives here. Diagnostic and treatment execution
 * share these operations; their declared values drive validation.
 */
export class Procedure {
  private state: ProcedureProps;
  private exclusiveWrite = false;

  get requiresExclusiveWrite(): boolean {
    return this.exclusiveWrite;
  }

  private constructor(props: ProcedureProps) {
    assertIdentity("id", props.id);
    if (props.kind !== "diagnostic" && props.kind !== "treatment")
      throw new InvalidInstanceError("Unknown procedure kind", { field: "kind" });
    if (props.declaration) {
      validateProcedureDeclaration(props.declaration);
      if (
        props.declaration.definitionUrl !== props.definition.url ||
        String(props.declaration.version) !== props.definition.version
      )
        throw new InvalidInstanceError("The declaration must match the pinned revision", { field: "definition" });
      for (const entry of props.observationLevels ?? []) {
        if (!props.declaration.observations.some((observation) => observation.id === entry.observationId))
          throw new InvalidInstanceError(`Unknown planned observation '${entry.observationId}'`);
      }
    }
    const planProblem = observationLevelsProblem(props.observationLevels);
    if (planProblem) throw new InvalidInstanceError(planProblem);
    if (props.origin.kind === "ordered") {
      assertIdentity("orderSetId", props.origin.orderSetId);
      assertIdentity("orderId", props.origin.orderId);
    } else if (props.origin.kind !== "unplanned") {
      throw new InvalidInstanceError("Unknown procedure origin", { field: "origin" });
    }
    assertIdentity("patientId", props.patientId);
    assertIdentity("visitId", props.visitId);
    if (!props.definition?.url?.trim() || !props.definition.version?.trim()) {
      throw new InvalidInstanceError("A procedure pins an exact definition revision, `url|version`", {
        definitionUrl: props.definition?.url,
        field: "definition",
      });
    }
    assertPerformers(props.performerIds);
    if (props.performedPeriod) assertPeriod(props.performedPeriod);
    if (props.status === "didNotPerform" || props.status === "enteredInError") {
      if (props.performedPeriod) {
        throw new InvalidInstanceError(`A '${props.status}' procedure has no performed period`, {
          status: props.status,
          field: "performedPeriod",
        });
      }
    }
    if (!new Set(["inProgress", ...TERMINAL]).has(props.status)) {
      throw new InvalidInstanceError("Unknown procedure status", { field: "status" });
    }
    if (props.status === "inProgress" && props.performedPeriod?.end) {
      throw new InvalidInstanceError("An in-progress procedure has no outcome attestation", { field: "status" });
    }
    if (
      (props.status === "didNotPerform" || props.status === "enteredInError") &&
      (props.trials.some(isLive) ||
        props.observations.some(isLive) ||
        props.uncaptured.length > 0 ||
        props.performerIds.length > 0)
    ) {
      throw new InvalidInstanceError("An unperformed procedure cannot assert clinical work", { field: "status" });
    }

    const trialIds = new Set<string>();
    for (const trial of props.trials) {
      assertTrialSnapshot(trial);
      if (trialIds.has(trial.id))
        throw new InvalidInstanceError(`Trial '${trial.id}' is recorded twice`, { memberId: trial.id });
      trialIds.add(trial.id);
    }
    const observed = new Set<string>();
    for (const observation of props.observations) {
      assertIdentity("observationId", observation.observationId);
      if (observed.has(observation.observationId)) {
        throw new InvalidInstanceError(`Observation '${observation.observationId}' is recorded twice`, {
          memberId: observation.observationId,
        });
      }
      observed.add(observation.observationId);
      Procedure.assertObservationScope(props, observation, !isLive(observation));
    }
    const closed = new Set<MemberId>();
    for (const definitionId of props.uncaptured) {
      assertIdentity("definitionId", definitionId);
      if (closed.has(definitionId)) {
        throw new InvalidInstanceError(`Observation '${definitionId}' is marked uncaptured twice`, {
          memberId: definitionId,
        });
      }
      closed.add(definitionId);
      if (props.observations.some((candidate) => isLive(candidate) && candidate.definitionId === definitionId)) {
        throw new InvalidInstanceError(`Observation '${definitionId}' is both recorded and marked uncaptured`, {
          memberId: definitionId,
        });
      }
    }

    this.state = structuredClone(props);
    if (props.declaration) {
      const definition = {
        url: props.declaration.definitionUrl,
        version: props.declaration.version,
        observations: props.declaration.observations,
        parameters: [],
      };
      for (const observation of props.observations) {
        const spec = this.requireSpec(definition, observation.definitionId);
        this.assertComponents(spec, observation);
        if (isLive(observation)) this.assertOccurrenceAvailable(spec, observation.observationId, observation.trialId);
      }
      for (const definitionId of props.uncaptured) this.requireSpec(definition, definitionId);
    }
  }

  static begin(facts: ProcedureBeginFacts): Procedure {
    const note = optionalText(facts.note);
    return new Procedure({
      id: asProcedureId(facts.id),
      kind: facts.kind,
      ...(facts.declaration ? { declaration: structuredClone(facts.declaration) } : {}),
      trials: [],
      ...(facts.observationLevels?.length ? { observationLevels: structuredClone(facts.observationLevels) } : {}),
      origin: creationOrigin(facts),
      patientId: facts.patientId,
      visitId: facts.visitId,
      definition: { ...facts.definition },
      status: "inProgress",
      performerIds: [...(facts.performerIds ?? [])],
      performedPeriod: facts.performedPeriod,
      ...(note === undefined ? {} : { note }),
      observations: [],
      uncaptured: [],
    });
  }

  static didNotPerform(facts: ProcedureCreationFacts): Procedure {
    const note = optionalText(facts.note);
    return new Procedure({
      id: asProcedureId(facts.id),
      kind: facts.kind,
      ...(facts.declaration ? { declaration: structuredClone(facts.declaration) } : {}),
      trials: [],
      ...(facts.observationLevels?.length ? { observationLevels: structuredClone(facts.observationLevels) } : {}),
      origin: creationOrigin(facts),
      patientId: facts.patientId,
      visitId: facts.visitId,
      definition: { ...facts.definition },
      status: "didNotPerform",
      performerIds: [],
      ...(note === undefined ? {} : { note }),
      observations: [],
      uncaptured: [],
    });
  }

  static restore(snapshot: ProcedureSnapshot): Procedure {
    return new Procedure(snapshot);
  }

  get orderId(): OrderId | undefined {
    return this.state.origin.kind === "ordered" ? this.state.origin.orderId : undefined;
  }

  get orderSetId(): OrderSetId | undefined {
    return this.state.origin.kind === "ordered" ? this.state.origin.orderSetId : undefined;
  }

  get kind(): ProcedureProps["kind"] {
    return this.state.kind;
  }

  get trials(): readonly ProcedureTrial[] {
    return structuredClone(this.state.trials);
  }

  get id(): ProcedureId {
    return this.state.id;
  }

  get origin(): ProcedureOrigin {
    return structuredClone(this.state.origin);
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  get visitId(): VisitId {
    return this.state.visitId;
  }

  get definition(): Readonly<VersionedCanonical> {
    return { ...this.state.definition };
  }

  get status(): ProcedureStatus {
    return this.state.status;
  }

  get note(): string | undefined {
    return this.state.note;
  }

  get performerIds(): readonly PractitionerId[] {
    return [...this.state.performerIds];
  }

  get performedPeriod(): ProcedurePerformedPeriod | undefined {
    return this.state.performedPeriod && { ...this.state.performedPeriod };
  }

  get observations(): readonly ProcedureRecordedObservation[] {
    return structuredClone(this.state.observations);
  }

  get uncaptured(): readonly string[] {
    return [...this.state.uncaptured];
  }

  observationOf(observationId: ObservationId): ProcedureRecordedObservation | undefined {
    const observation = this.state.observations.find((candidate) => candidate.observationId === observationId);
    return observation && (structuredClone(observation) as ProcedureRecordedObservation);
  }

  isUncaptured(definitionId: MemberId): boolean {
    return this.state.uncaptured.includes(definitionId);
  }

  get isResolved(): boolean {
    return TERMINAL.has(this.state.status);
  }

  snapshot(): ProcedureSnapshot {
    return structuredClone(this.state);
  }

  complete(definition: ProcedureDefinitionSpec, facts: ProcedureCompletionFacts): void {
    this.assertStatus("complete");
    this.finish("performed", definition, facts);
  }

  unableToComplete(definition: ProcedureDefinitionSpec, facts: ProcedureCompletionFacts): void {
    this.assertStatus("unableToComplete");
    this.finish("unableToComplete", definition, facts);
  }

  reopen(): void {
    const period = this.state.performedPeriod;
    this.transition("reopen", {
      performedPeriod: period
        ? {
            start: period.start,
            ...(period.startSource ? { startSource: period.startSource } : {}),
          }
        : undefined,
    });
  }

  adjustTimes(times: { start?: string; end?: string }, now: string): void {
    const period = this.state.performedPeriod;
    if (times.start === undefined && times.end === undefined) {
      throw new InvalidInstanceError("Supply a start or end time", { field: "performedPeriod" });
    }
    if (times.end !== undefined && this.state.status === "inProgress") {
      throw new InvalidInstanceTransitionError("An in-progress procedure cannot have an end time", {
        status: this.state.status,
      });
    }
    const start = times.start ?? period?.start;
    const end = times.end ?? period?.end;
    if (!start) throw new InvalidInstanceError("Supply a start time", { field: "performedPeriod" });
    assertPeriod({ start, end });
    for (const value of [start, end]) {
      if (value !== undefined && Date.parse(value) > Date.parse(now) + 120_000) {
        throw new InvalidInstanceError("Procedure times cannot be more than two minutes in the future", {
          field: "performedPeriod",
        });
      }
    }
    const startSource = times.start !== undefined ? "manual" : period?.startSource;
    const endSource = end === undefined ? undefined : times.end !== undefined ? "manual" : period?.endSource;
    this.state = new Procedure({
      ...this.state,
      performedPeriod: {
        start,
        ...(end === undefined ? {} : { end }),
        ...(startSource ? { startSource } : {}),
        ...(endSource ? { endSource } : {}),
      },
    }).state;
  }

  retract(): void {
    this.transition("retract");
  }

  reinstateDidNotPerform(): void {
    this.transition("reinstateDidNotPerform");
  }

  resume(): void {
    this.transition("resume");
  }

  markDidNotPerform(): void {
    this.assertStatus("markDidNotPerform");
    if (this.state.trials.some(isLive) || this.state.observations.some(isLive) || this.state.uncaptured.length > 0) {
      throw new InvalidInstanceTransitionError(
        "This procedure has charting on it. Choose 'Unable to complete' if the work stopped; what was recorded will be kept.",
        { status: this.state.status },
      );
    }
    this.transition("markDidNotPerform", { performedPeriod: undefined, performerIds: [] });
  }

  setNote(note?: string): void {
    this.state.note = optionalText(note);
  }

  recordTrials(definition: ProcedureTrialDefinitionSpec, trials: readonly ProcedureTrialFacts[]): void {
    const previous = this.state.trials;
    const exclusiveWrite = this.exclusiveWrite;
    this.state.trials = [...previous];
    try {
      for (const trial of trials) this.recordTrial(definition, trial);
    } catch (error) {
      this.state.trials = previous;
      this.exclusiveWrite = exclusiveWrite;
      throw error;
    }
  }

  recordTrial(definition: ProcedureTrialDefinitionSpec, facts: ProcedureTrialFacts): void {
    this.assertChartable("record a trial");
    assertTrialSnapshot(facts);
    if (this.state.trials.some((trial) => trial.id === facts.id)) {
      throw new InvalidInstanceError(`Trial '${facts.id}' has already been recorded`, { memberId: facts.id });
    }
    this.assertTrial(definition, facts);
    this.state.trials.push({
      ...structuredClone(facts),
      values: structuredClone([...facts.values]),
      lifecycle: { status: "Final" },
    });
    this.exclusiveWrite = true;
  }

  correctTrial(
    definition: ProcedureTrialDefinitionSpec,
    id: ObservationId,
    facts: Omit<ProcedureTrialFacts, "id" | "derivedFromDocumentId" | "plannedTrialId"> & {
      /** Omitted keeps the link, null clears it. */
      plannedTrialId?: PlannedTrialId | null;
    },
  ): void {
    this.assertChartable("correct a trial");
    const trial = this.requireTrial(id);
    const { plannedTrialId, ...rest } = facts;
    const relinked = plannedTrialId !== undefined && plannedTrialId !== trial.plannedTrialId;
    this.assertTrial(definition, { ...rest, ...(relinked && plannedTrialId ? { plannedTrialId } : {}), id });
    if (plannedTrialId === null) delete trial.plannedTrialId;
    else if (plannedTrialId) trial.plannedTrialId = plannedTrialId;
    trial.effectiveDateTime = facts.effectiveDateTime;
    trial.values = structuredClone([...facts.values]);
    trial.lifecycle = { status: "Corrected" };
    this.exclusiveWrite = true;
  }

  trialsDerivedFrom(documentId: PatientDocumentId): readonly ObservationId[] {
    return this.state.trials
      .filter((trial) => trial.lifecycle.status !== "EnteredInError" && trial.derivedFromDocumentId === documentId)
      .map((trial) => trial.id);
  }

  retractTrial(id: ObservationId): void {
    this.assertChartable("retract a trial");
    this.requireTrial(id).lifecycle = { status: "EnteredInError" };
    for (const reading of this.state.observations) {
      if (reading.trialId === id) reading.lifecycle = { status: "EnteredInError" };
    }
    this.exclusiveWrite = true;
  }

  private requireTrial(id: ObservationId): ProcedureTrial {
    const trial = this.state.trials.find((candidate) => candidate.id === id);
    if (!trial)
      throw new UnknownMemberError(`Trial '${id}' has not been recorded`, { memberId: id, collection: "trials" });
    if (trial.lifecycle.status === "EnteredInError")
      throw new InvalidInstanceError(`Trial '${id}' is entered in error`, { memberId: id });
    return trial;
  }

  private assertTrial(definition: ProcedureTrialDefinitionSpec, facts: ProcedureTrialFacts): void {
    if (definition.url !== this.state.definition.url || String(definition.version) !== this.state.definition.version) {
      throw new InvalidInstanceError("Trial definition must match the procedure's pinned revision", {
        definitionUrl: definition.url,
        definitionVersion: definition.version,
      });
    }
    if (facts.plannedTrialId) {
      if (!definition.plannedTrials?.some((plan) => plan.id === facts.plannedTrialId))
        throw new UnknownMemberError(`Unknown planned trial '${facts.plannedTrialId}'`, {
          memberId: facts.plannedTrialId,
          collection: "plannedTrials",
        });
    }
    assertTrialValues(definition, facts);
  }

  recordObservation(
    definition: ProcedureDefinitionSpec,
    facts: {
      trialId?: ObservationId;
      derivedFromDocumentId?: PatientDocumentId;
      observationId: ObservationId;
      definitionId: MemberId;
      observedAt: string;
      components: readonly ProcedureObservationComponent[];
      note?: string;
    },
  ): void {
    this.assertChartable("record an observation");
    assertIdentity("observationId", facts.observationId);
    if (this.state.observations.some((candidate) => candidate.observationId === facts.observationId)) {
      throw new InvalidInstanceError(`Observation '${facts.observationId}' has already been recorded`, {
        memberId: facts.observationId,
      });
    }
    const spec = this.requireSpec(definition, facts.definitionId);
    this.assertComponents(spec, facts);
    Procedure.assertObservationScope(this.state, facts);
    this.assertSlotOpen(spec.id);
    this.assertOccurrenceAvailable(spec, undefined, facts.trialId);
    const note = optionalText(facts.note);
    this.state.observations.push({
      observationId: facts.observationId,
      ...(facts.trialId ? { trialId: facts.trialId } : {}),
      ...(facts.derivedFromDocumentId
        ? { derivedFrom: { documentId: facts.derivedFromDocumentId, writtenBy: "Document" as const } }
        : {}),
      definitionId: facts.definitionId,
      observedAt: facts.observedAt,
      components: structuredClone(facts.components) as ProcedureObservationComponent[],
      ...(note === undefined ? {} : { note }),
      lifecycle: { status: "Final" },
    });
    this.exclusiveWrite = true;
  }

  correctObservation(
    definition: ProcedureDefinitionSpec,
    observationId: ObservationId,
    facts: {
      trialId?: ObservationId;
      derivedFromDocumentId?: PatientDocumentId;
      definitionId: MemberId;
      observedAt: string;
      components: readonly ProcedureObservationComponent[];
      note?: string;
    },
  ): void {
    this.assertChartable("correct an observation");
    const existing = this.requireCurrent(observationId);
    const spec = this.requireSpec(definition, facts.definitionId);
    this.assertComponents(spec, { observationId, ...facts });
    if (spec.id !== existing.definitionId) this.assertSlotOpen(spec.id);
    Procedure.assertObservationScope(this.state, { definitionId: facts.definitionId, trialId: existing.trialId });
    this.assertOccurrenceAvailable(spec, observationId, existing.trialId);
    existing.derivedFrom = facts.derivedFromDocumentId
      ? { documentId: facts.derivedFromDocumentId, writtenBy: "Document" }
      : existing.derivedFrom
        ? { ...existing.derivedFrom, writtenBy: "Hand" }
        : undefined;
    existing.definitionId = facts.definitionId;
    existing.observedAt = facts.observedAt;
    existing.components = structuredClone(facts.components) as ProcedureObservationComponent[];
    existing.note = optionalText(facts.note);
    existing.lifecycle = { status: "Corrected" };
    this.exclusiveWrite = true;
  }

  markObservationEnteredInError(observationId: ObservationId): void {
    const observation = this.requireCurrent(observationId);
    observation.lifecycle = { status: "EnteredInError" };
    this.exclusiveWrite = true;
  }

  markObservationUnableToCapture(definition: ProcedureDefinitionSpec, definitionId: MemberId): void {
    this.assertChartable("mark an observation unable to capture");
    const spec = this.requireSpec(definition, definitionId);
    if (this.state.observations.some((candidate) => isLive(candidate) && candidate.definitionId === spec.id)) {
      throw new InvalidInstanceError(`Observation '${spec.id}' has a recorded reading and was captured`, {
        memberId: spec.id,
      });
    }
    if (!this.state.uncaptured.includes(spec.id)) {
      this.state.uncaptured.push(spec.id);
      this.exclusiveWrite = true;
    }
  }

  clearObservationUnableToCapture(definition: ProcedureDefinitionSpec, definitionId: MemberId): void {
    this.assertChartable("clear unable to capture");
    const spec = this.requireSpec(definition, definitionId);
    if (this.state.uncaptured.includes(spec.id)) {
      this.state.uncaptured = this.state.uncaptured.filter((id) => id !== spec.id);
      this.exclusiveWrite = true;
    }
  }

  recordObservations(
    definition: ProcedureDefinitionSpec,
    readings: readonly Parameters<Procedure["recordObservation"]>[1][],
  ): void {
    const previous = this.state.observations;
    const exclusiveWrite = this.exclusiveWrite;
    this.state.observations = [...previous];
    try {
      for (const reading of readings) this.recordObservation(definition, reading);
    } catch (error) {
      this.state.observations = previous;
      this.exclusiveWrite = exclusiveWrite;
      throw error;
    }
  }

  answeredObservationsFor(definition: ProcedureDefinitionSpec, trialId?: ObservationId): readonly string[] {
    this.assertPinned(definition);
    return definition.observations
      .filter(
        (declared) =>
          Boolean(this.state.observationLevels?.some((entry) => entry.observationId === declared.id)) ===
            (trialId !== undefined) &&
          (this.isUncaptured(declared.id) ||
            (!declared.repeatable &&
              this.state.observations.some(
                (observation) =>
                  isLive(observation) && observation.definitionId === declared.id && observation.trialId === trialId,
              ))),
      )
      .map((declared) => declared.id);
  }

  recordOutcome(definition: ProcedureDefinitionSpec, facts: ProcedureOutcomeFacts): void {
    this.assertPinned(definition);
    if (
      facts.patientId !== this.patientId ||
      facts.visitId !== this.visitId ||
      this.state.origin.kind !== "ordered" ||
      facts.orderId !== this.state.origin.orderId
    )
      throw new InvalidInstanceTransitionError("Procedure belongs to different outcome facts");
    if (this.status !== "inProgress") {
      if (
        this.status !== facts.outcome ||
        this.note !== optionalText(facts.note) ||
        (facts.performerIds !== undefined && JSON.stringify(this.performerIds) !== JSON.stringify(facts.performerIds))
      )
        throw new InvalidInstanceTransitionError("Procedure belongs to different outcome facts");
      return;
    }
    if (facts.outcome === "didNotPerform") this.markDidNotPerform();
    else if (facts.outcome === "performed") this.complete(definition, facts);
    else this.unableToComplete(definition, facts);
    if (facts.note !== undefined) this.setNote(facts.note);
  }

  observationsDerivedFrom(documentId: PatientDocumentId, trialId?: ObservationId): readonly string[] {
    return [
      ...new Set(
        this.state.observations
          .filter(
            (observation) =>
              isLive(observation) &&
              observation.derivedFrom?.documentId === documentId &&
              observation.trialId === trialId,
          )
          .map((observation) => observation.definitionId),
      ),
    ];
  }

  private static assertObservationScope(
    state: ProcedureProps,
    facts: { definitionId: MemberId; trialId?: ObservationId },
    historical = false,
  ): void {
    const trialLevel = state.observationLevels?.some((entry) => entry.observationId === facts.definitionId);
    if (trialLevel) {
      const trial = state.trials.find((trial) => trial.id === facts.trialId);
      if (!trial || (!historical && !isLive(trial)))
        throw new InvalidInstanceError("Observation requires a recorded live trial", { memberId: facts.definitionId });
    } else if (facts.trialId !== undefined) {
      throw new InvalidInstanceError("Observation is planned at procedure level", { memberId: facts.definitionId });
    }
  }

  private finish(
    status: "performed" | "unableToComplete",
    definition: ProcedureDefinitionSpec,
    facts: ProcedureCompletionFacts,
  ): void {
    this.assertPinned(definition);
    if (facts.performerIds) assertPerformers(facts.performerIds);
    this.transition(status === "performed" ? "complete" : "unableToComplete", {
      performerIds: facts.performerIds ? [...facts.performerIds] : this.state.performerIds,
    });
  }

  private assertComponents(
    spec: ProcedureObservationSpec,
    facts: { observationId: ObservationId; observedAt: string; components: readonly ProcedureObservationComponent[] },
  ): void {
    if (Number.isNaN(Date.parse(facts.observedAt))) {
      throw new InvalidInstanceError(`Observation '${facts.observationId}' has an invalid clinical time`, {
        memberId: facts.observationId,
        field: "observedAt",
      });
    }
    if (facts.components.length === 0) {
      throw new InvalidInstanceError(`Observation '${facts.observationId}' requires at least one component`, {
        memberId: facts.observationId,
      });
    }
    const seen = new Set<MemberId>();
    for (const component of facts.components) {
      if (seen.has(component.componentId)) {
        throw new InvalidInstanceError(`Component '${component.componentId}' is answered twice`, {
          memberId: component.componentId,
        });
      }
      seen.add(component.componentId);
      const declared = spec.components.find((candidate) => candidate.id === component.componentId);
      if (!declared) {
        throw new UnknownMemberError(`Observation '${spec.id}' does not declare component '${component.componentId}'`, {
          memberId: component.componentId,
          collection: "components",
        });
      }
      const problem = answeredValueProblem(declared.value, component.value);
      if (problem) {
        throw new InvalidInstanceError(`Component '${component.componentId}' ${problem}`, {
          memberId: component.componentId,
        });
      }
    }
  }

  private assertSlotOpen(definitionId: MemberId): void {
    if (this.state.uncaptured.includes(definitionId)) {
      throw new InvalidInstanceError(`Observation '${definitionId}' is marked as not captured`, {
        memberId: definitionId,
      });
    }
  }

  private assertOccurrenceAvailable(spec: ProcedureObservationSpec, excluding?: string, trialId?: ObservationId): void {
    if (spec.repeatable) return;
    const taken = this.state.observations.some(
      (candidate) =>
        candidate.observationId !== excluding &&
        isLive(candidate) &&
        candidate.definitionId === spec.id &&
        candidate.trialId === trialId,
    );
    if (taken) {
      throw new InvalidInstanceError(`Observation '${spec.id}' is not repeatable and is already recorded`, {
        memberId: spec.id,
      });
    }
  }

  private assertPinned(definition: Pick<ProcedureDefinitionSpec, "url" | "version">): void {
    if (definition.url !== this.state.definition.url || String(definition.version) !== this.state.definition.version) {
      throw new InvalidInstanceError(
        `This procedure is pinned to ${this.state.definition.url}|${this.state.definition.version}, not ${definition.url}|${definition.version}`,
        { definitionUrl: definition.url, definitionVersion: definition.version },
      );
    }
  }

  private requireSpec(definition: ProcedureDefinitionSpec, definitionId: MemberId): ProcedureObservationSpec {
    this.assertPinned(definition);
    const spec = definition.observations.find((candidate) => candidate.id === definitionId);
    if (!spec) {
      throw new UnknownMemberError(`No observation '${definitionId}' in this procedure definition`, {
        memberId: definitionId,
        collection: "observations",
      });
    }
    return spec;
  }

  private requireCurrent(observationId: ObservationId): ProcedureRecordedObservation {
    const observation = this.state.observations.find((candidate) => candidate.observationId === observationId);
    if (!observation) {
      throw new UnknownMemberError(`Observation '${observationId}' has not been recorded`, {
        memberId: observationId,
        collection: "observations",
      });
    }
    if (!isLive(observation)) {
      throw new InvalidInstanceError(`Observation '${observationId}' is entered in error`, { memberId: observationId });
    }
    return observation;
  }

  private assertChartable(action: string): void {
    if (this.state.status === "didNotPerform" || this.state.status === "enteredInError") {
      throw new InvalidInstanceTransitionError(`Cannot ${action} on a '${this.state.status}' procedure`, {
        status: this.state.status,
      });
    }
  }

  private assertStatus(action: ProcedureAction): void {
    if (!TRANSITIONS[action].from.includes(this.status)) {
      throw new InvalidInstanceTransitionError(`Cannot ${action} a procedure that is '${this.status}'`, {
        status: this.status,
      });
    }
  }

  private transition(action: ProcedureAction, patch: Partial<ProcedureProps> = {}): void {
    this.assertStatus(action);
    this.state = new Procedure({ ...this.state, ...patch, status: TRANSITIONS[action].to }).state;
  }
}

function isLive(observation: { lifecycle: { status: string } }): boolean {
  return observation.lifecycle.status !== "EnteredInError";
}

function assertPerformers(performerIds: readonly PractitionerId[]): void {
  const seen = new Set<string>();
  for (const performerId of performerIds) {
    assertIdentity("performerId", performerId);
    if (seen.has(performerId)) {
      throw new InvalidInstanceError(`Performer '${performerId}' is named twice`, { field: "performerIds" });
    }
    seen.add(performerId);
  }
}

function assertPeriod(period: ProcedurePerformedPeriod): void {
  assertClinicalTime(period.start, "start");
  if (period.end === undefined) return;
  assertClinicalTime(period.end, "end");
  if (Date.parse(period.end) < Date.parse(period.start)) {
    throw new InvalidInstanceError("A procedure cannot end before it started", { field: "performedPeriod" });
  }
}

function assertClinicalTime(value: string, which: string): void {
  if (Number.isNaN(Date.parse(value))) {
    throw new InvalidInstanceError(`A procedure's ${which} time is invalid`, { field: "performedPeriod" });
  }
}

function assertIdentity(field: string, value: string): void {
  if (!value?.trim()) {
    throw new InvalidInstanceError(`A procedure requires a nonblank ${field}`, { field });
  }
}

function optionalText(value?: string): string | undefined {
  return value?.trim() ? value : undefined;
}

/** Order authorization is evaluated at creation, not re-evaluated on historical reads. */
function creationOrigin(input: ProcedureCreationFacts): ProcedureOrigin {
  const origin = input.origin;
  if (origin.kind === "unplanned") return { kind: "unplanned" };
  const facts = origin.facts;
  if (!facts) throw new InvalidInstanceError("An ordered procedure requires order facts", { field: "origin" });
  if (!facts.executable || facts.alreadyFulfilled) {
    throw new InvalidInstanceError("The order must be executable and not already fulfilled", { field: "origin" });
  }
  if (
    facts.patientId !== input.patientId ||
    facts.visitId !== input.visitId ||
    facts.definition.url !== input.definition.url ||
    facts.definition.version !== input.definition.version
  ) {
    throw new InvalidInstanceError("The order must match the patient, visit, and definition revision", {
      field: "origin",
    });
  }
  return { kind: "ordered", orderSetId: origin.orderSetId, orderId: origin.orderId };
}

function validateProcedureDeclaration(input: ProcedureDeclaration): ProcedureDeclaration {
  requireIdentity(input.definitionUrl, "Diagnostic declaration requires a definition URL", "definitionUrl");
  if (!Number.isInteger(input.version) || input.version < 1) {
    throw new InvalidDiagnosticProcedureIdentityError("Diagnostic declaration requires a positive version", {
      field: "version",
    });
  }
  requireIdentity(input.name, "Diagnostic declaration requires a name", "name");
  const observationIds = new Set<string>();
  for (const observation of input.observations) {
    requireIdentity(observation.id, "Diagnostic observation declaration requires identity", "observationId");
    if (observationIds.has(observation.id)) {
      throw new InvalidDiagnosticProcedureIdentityError(`Diagnostic observation '${observation.id}' appears twice`, {
        field: "observationId",
      });
    }
    observationIds.add(observation.id);
    requireIdentity(observation.label, "Diagnostic observation declaration requires a label", "observationLabel");
    if (observation.components.length === 0) {
      throw new InvalidDiagnosticProcedureIdentityError(
        `Diagnostic observation '${observation.id}' requires at least one component`,
        { field: "components" },
      );
    }
    const componentIds = new Set<string>();
    for (const component of observation.components) {
      requireIdentity(component.id, "Diagnostic component declaration requires identity", "componentId");
      if (componentIds.has(component.id)) {
        throw new InvalidDiagnosticProcedureIdentityError(`Diagnostic component '${component.id}' appears twice`, {
          field: "componentId",
        });
      }
      componentIds.add(component.id);
      requireIdentity(component.label, "Diagnostic component declaration requires a label", "componentLabel");
      validateValue(component.value, { memberId: component.id, collection: "diagnostic observations" });
      if (component.value.kind === "Choice") {
        for (const option of component.value.options) {
          requireIdentity(option.id ?? "", "Diagnostic choice option requires identity", "optionId");
        }
      }
    }
  }
  return structuredClone(input);
}

function requireIdentity(value: string, message: string, field: string): void {
  if (!value.trim()) throw new InvalidDiagnosticProcedureIdentityError(message, { field });
}
